'use strict'
import React, { useCallback, useMemo, useState } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  TextField,
  CircularProgress,
  Typography,
  FormHelperText
} from '@material-ui/core'
import CutCrewImpactSummary from './CutCrewImpactSummary'
import CutCrewPreviewTable from './CutCrewPreviewTable'

const useStyles = makeStyles(theme => ({
  tableContainer: {
    maxHeight: 320,
    overflow: 'auto',
    marginBottom: theme.spacing(2)
  },
  optionsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: theme.spacing(2)
  },
  strategySelect: {
    minWidth: 200
  },
  capacityField: {
    width: 140
  },
  errorBox: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main
  }
}))

const STEPS = ['Select Route', 'Preview & Apply']

const STRATEGY_OPTIONS = [
  { value: 'leave_unassigned', label: 'Leave all displaced unassigned' },
  { value: 'capacity_balance', label: 'Auto-balance by capacity' },
  { value: 'nearest_first', label: 'Nearest active crew first' }
]

export default function CutCrewModal ({ open, onClose, url, scenarioId, routes, onApplied }) {
  const classes = useStyles()

  const [activeStep, setActiveStep] = useState(0)
  const [selectedRouteId, setSelectedRouteId] = useState('')
  const [strategy, setStrategy] = useState('leave_unassigned')
  const [prioritizeVip, setPrioritizeVip] = useState(true)
  const [respectCapacity, setRespectCapacity] = useState(true)
  const [recomputeMetrics, setRecomputeMetrics] = useState(true)
  const [maxWeeklyMinutes, setMaxWeeklyMinutes] = useState(2400)

  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const [previewError, setPreviewError] = useState(null)

  const [applyLoading, setApplyLoading] = useState(false)
  const [applyError, setApplyError] = useState(null)

  const [routeMetrics, setRouteMetrics] = useState(null)

  const activeRoutes = useMemo(() =>
    (routes || []).filter(r => r.crewId),
    [routes]
  )

  React.useEffect(() => {
    if (!open || !scenarioId) return
    let cancelled = false
    axios.get(`${url}/api/hmi/territory/scenarios/${scenarioId}/metrics`)
      .then(res => {
        if (cancelled) return
        const crewMetrics = res.data?.crews || []
        const byCrewId = new Map(crewMetrics.map(c => [c.crewId, c]))
        setRouteMetrics(byCrewId)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [open, scenarioId, url])

  const resetState = useCallback(() => {
    setActiveStep(0)
    setSelectedRouteId('')
    setStrategy('leave_unassigned')
    setPrioritizeVip(true)
    setRespectCapacity(true)
    setRecomputeMetrics(true)
    setMaxWeeklyMinutes(2400)
    setPreviewData(null)
    setPreviewError(null)
    setPreviewLoading(false)
    setApplyLoading(false)
    setApplyError(null)
    setRouteMetrics(null)
  }, [])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [onClose, resetState])

  const buildPayload = useCallback(() => ({
    cutRouteId: selectedRouteId,
    strategy,
    prioritizeVip,
    respectCapacity,
    maxWeeklyMinutesPerCrew: Number(maxWeeklyMinutes) || 2400,
    recomputeMetrics
  }), [selectedRouteId, strategy, prioritizeVip, respectCapacity, maxWeeklyMinutes, recomputeMetrics])

  const handlePreview = useCallback(async () => {
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const response = await axios.post(
        `${url}/api/hmi/territory/scenarios/${scenarioId}/cut-crew/preview`,
        buildPayload()
      )
      setPreviewData(response.data)
      setActiveStep(1)
    } catch (err) {
      setPreviewError(err.response?.data?.message || err.message || 'Preview failed')
    } finally {
      setPreviewLoading(false)
    }
  }, [url, scenarioId, buildPayload])

  const handleApply = useCallback(async () => {
    setApplyLoading(true)
    setApplyError(null)
    try {
      const response = await axios.post(
        `${url}/api/hmi/territory/scenarios/${scenarioId}/cut-crew`,
        buildPayload()
      )
      const summary = response.data?.summary
      const message = summary
        ? `Crew cut applied: ${summary.displacedCount} displaced, ${summary.reassignedCount} reassigned, ${summary.unassignedCount} unassigned.`
        : 'Crew cut applied.'
      handleClose()
      if (onApplied) onApplied(message, response.data)
    } catch (err) {
      setApplyError(err.response?.data?.message || err.message || 'Apply failed')
      setApplyLoading(false)
    }
  }, [url, scenarioId, buildPayload, handleClose, onApplied])

  const renderSelectStep = () => (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Route Name</TableCell>
              <TableCell>Crew Leader</TableCell>
              <TableCell>Linked Crew</TableCell>
              <TableCell align="right">Properties</TableCell>
              <TableCell align="right">Weekly Min</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeRoutes.map(route => {
              const metrics = routeMetrics?.get(route.crewId)
              return (
                <TableRow
                  key={route._id}
                  hover
                  selected={selectedRouteId === route._id}
                  onClick={() => setSelectedRouteId(route._id)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      checked={selectedRouteId === route._id}
                      onChange={() => setSelectedRouteId(route._id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{route.name}</TableCell>
                  <TableCell>{route.crewLeader || '—'}</TableCell>
                  <TableCell>{route.crewName || '—'}</TableCell>
                  <TableCell align="right">{metrics?.propertyCount ?? '—'}</TableCell>
                  <TableCell align="right">{metrics?.weeklyServiceMinutes ?? '—'}</TableCell>
                </TableRow>
              )
            })}
            {activeRoutes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">No active routes with linked crews</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.optionsRow}>
        <FormControl variant="outlined" size="small" className={classes.strategySelect}>
          <InputLabel>Strategy</InputLabel>
          <Select value={strategy} onChange={e => setStrategy(e.target.value)} label="Strategy">
            {STRATEGY_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Default keeps every displaced property unassigned for manual review.
          </FormHelperText>
        </FormControl>
        <FormControlLabel
          control={<Checkbox checked={prioritizeVip} onChange={e => setPrioritizeVip(e.target.checked)} size="small" />}
          label="Prioritize VIP"
        />
        <FormControlLabel
          control={<Checkbox checked={respectCapacity} onChange={e => setRespectCapacity(e.target.checked)} size="small" />}
          label="Respect capacity"
        />
        <FormControlLabel
          control={<Checkbox checked={recomputeMetrics} onChange={e => setRecomputeMetrics(e.target.checked)} size="small" />}
          label="Recompute metrics"
        />
        <TextField
          label="Max weekly min"
          type="number"
          variant="outlined"
          size="small"
          className={classes.capacityField}
          value={maxWeeklyMinutes}
          onChange={e => setMaxWeeklyMinutes(e.target.value)}
        />
      </div>
      {previewError && (
        <Typography className={classes.errorBox} variant="body2">{previewError}</Typography>
      )}
    </>
  )

  const renderPreviewStep = () => (
    <>
      <CutCrewImpactSummary
        cutRoute={previewData?.cutRoute}
        displaced={previewData?.displaced}
        suggestedMovesCount={previewData?.suggestedMoves?.length || 0}
        unassignedCount={previewData?.unassigned?.length || 0}
        warnings={previewData?.warnings}
      />
      <CutCrewPreviewTable
        suggestedMoves={previewData?.suggestedMoves}
        unassigned={previewData?.unassigned}
      />
      {applyError && (
        <Typography className={classes.errorBox} variant="body2">{applyError}</Typography>
      )}
    </>
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Cut Crew</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel style={{ paddingBottom: 16 }}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && renderSelectStep()}
        {activeStep === 1 && renderPreviewStep()}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePreview}
              disabled={!selectedRouteId || previewLoading}
            >
              {previewLoading ? <CircularProgress size={20} /> : 'Preview Impact'}
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="outlined" onClick={() => { setActiveStep(0); setPreviewError(null) }}>Back</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApply}
              disabled={applyLoading}
            >
              {applyLoading ? <CircularProgress size={20} /> : 'Apply Cut Crew'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
